/**
 * Admin-only moderation + notes section for the user details dialog.
 * Chat ladder: Warn (notice only) → Restrict (mute) → Lift.
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ShieldWarningIcon as ShieldWarning, NotePencilIcon as NotePencil, TrashIcon as Trash } from "@phosphor-icons/react";

export const AdminUserModerationSection = ({ userId, notes: initialNotes, onNotesChanged }) => {
  const [mod, setMod] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(initialNotes || []);
  const [noteText, setNoteText] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/chat/users/${userId}/moderation`)
      .then((r) => { setMod(r.data.chat_moderation); setHistory(r.data.history || []); })
      .catch(() => {});
  }, [userId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setNotes(initialNotes || []); }, [initialNotes]);

  const act = async (action) => {
    const labels = { warn: "Warning reason", restrict: "Restriction reason", unrestrict: "Note (optional)" };
    const reason = window.prompt(`${labels[action]} — the member will be notified by inbox + email:`);
    if (action !== "unrestrict" && (!reason || reason.trim().length < 3)) return;
    setBusy(true);
    try {
      const body = action === "unrestrict" ? { note: reason || "" } : { reason: reason.trim() };
      await api.post(`/admin/chat/users/${userId}/${action}`, body);
      toast.success(action === "warn" ? "Warning sent." : action === "restrict" ? "Chat restricted." : "Restriction lifted.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/data/users/${userId}/notes`, { text });
      setNotes((n) => [r.data.note, ...n]);
      setNoteText("");
      onNotesChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save note."); }
    finally { setBusy(false); }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/admin/data/users/${userId}/notes/${noteId}`);
      setNotes((n) => n.filter((x) => x.id !== noteId));
    } catch { toast.error("Delete failed."); }
  };

  const status = mod?.status || "none";
  return (
    <section data-testid="admin-user-moderation-section">
      <div className="flex items-center gap-2 mb-2">
        <ShieldWarning size={14} weight="duotone" className="text-ink/65" />
        <h3 className="font-semibold text-ink text-[14px]">Chat moderation & notes</h3>
        {status === "restricted" && <Badge className="bg-crimson text-white">Chat restricted</Badge>}
        {status === "warned" && <Badge className="bg-[#C9A227]/25 text-[#8A6A00] border-[#C9A227]/40 hover:bg-[#C9A227]/25">Warned</Badge>}
      </div>
      <div className="rounded-xl bg-stone-50 border border-stone-200 p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act("warn")}
            className="rounded-full h-8 text-[12px] border-[#C9A227]/50 text-[#8A6A00]" data-testid="admin-user-warn-btn">
            Warn
          </Button>
          {status !== "restricted" ? (
            <Button size="sm" disabled={busy} onClick={() => act("restrict")}
              className="rounded-full h-8 text-[12px] bg-crimson hover:bg-ink text-white" data-testid="admin-user-restrict-btn">
              Restrict chat (mute)
            </Button>
          ) : (
            <Button size="sm" disabled={busy} onClick={() => act("unrestrict")}
              className="rounded-full h-8 text-[12px] bg-emerald-700 hover:bg-ink text-white" data-testid="admin-user-unrestrict-btn">
              Lift restriction
            </Button>
          )}
        </div>
        <p className="text-[11px] text-ink/55 leading-snug">
          Warn = official notice (inbox + push + email), messaging still works. Restrict = they can read
          chats but can't start or send. Every action is logged and the member is notified.
        </p>
        {history.length > 0 && (
          <details>
            <summary className="text-[11px] text-ink/55 cursor-pointer">{history.length} moderation action(s) on file</summary>
            <ul className="mt-1.5 text-[11px] text-ink/70 space-y-0.5 pl-2">
              {history.slice().reverse().map((h, i) => (
                <li key={i}>
                  <strong>{h.action}</strong> — {h.reason || "(no reason)"} · {h.by_name} · {h.at ? new Date(h.at).toLocaleString() : ""}
                </li>
              ))}
            </ul>
          </details>
        )}

        <div className="pt-2 border-t border-stone-200">
          <div className="flex items-center gap-1.5 mb-1.5 text-[12px] font-semibold text-ink">
            <NotePencil size={12} weight="duotone" /> Admin notes <span className="font-normal text-ink/45">(never visible to the member)</span>
          </div>
          <div className="flex gap-2">
            <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={1}
              placeholder="Add a private note…" className="flex-1 resize-none text-[12px]" data-testid="admin-user-note-input" />
            <Button size="sm" disabled={busy || !noteText.trim()} onClick={addNote}
              className="rounded-full h-8 text-[12px] bg-ink hover:bg-crimson text-white" data-testid="admin-user-note-save">
              Save
            </Button>
          </div>
          {notes.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {notes.map((n) => (
                <li key={n.id} className="text-[11.5px] bg-white border border-stone-200 rounded-lg px-2.5 py-1.5 flex items-start gap-2" data-testid={`admin-user-note-${n.id}`}>
                  <span className="flex-1 whitespace-pre-wrap">{n.text}</span>
                  <span className="text-[10px] text-ink/45 flex-shrink-0">{n.by_name} · {n.created_at ? new Date(n.created_at).toLocaleDateString() : ""}</span>
                  <button type="button" onClick={() => deleteNote(n.id)} className="text-ink/35 hover:text-crimson flex-shrink-0" data-testid={`admin-user-note-delete-${n.id}`}>
                    <Trash size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

export default AdminUserModerationSection;
